import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWindowSize } from '@uidotdev/usehooks'
import { getAgent } from 'utils/agent'
import { fetchApp, fetchAppId } from '../../utils/io'
import { getPreviewType } from '../../utils/previewType'
import { getAdaloAttributionSettings } from '../../utils/adaloAttribution'
import { MIN_SCREEN_WIDTH_SAFE_FOR_FRAME } from '../../utils/frame'

import Loading from '../Shared/Loading'
import Renderer from '../Renderer'
import Frame from './Frame'
import { WEB, DEFAULT_SIZE } from './SizeMenu'
import AppDetails from './AppDetails'
import AdaloBadge from './AdaloBadge'
import NotFound from './NotFound'
import Stripe from './Stripe'

import './Share.css'
import './WrappedSelect.css'
import { shouldUseNewReactNative } from '../../utils/libraries'

const Preview = () => {
  const { path, route } = useParams()
  const { width: windowWidth } = useWindowSize()
  const [app, setApp] = useState(null)
  const [error, setError] = useState(false)
  const [redirecting, setRedirecting] = useState(false)

  const getAppId = async () => {
    // in prod the web-preview-proxy adds the app id to the html
    const htmlAppId = document.querySelector('meta[property="adalo:appId"]')
      ?.content

    if (htmlAppId && htmlAppId !== 'APP_ID') {
      return htmlAppId
    }

    // locally or on staging: call backend to resolve app id with subdomain & path
    const host = window.location.host

    try {
      const appId = await fetchAppId(host, path)

      return appId
    } catch (error) {
      console.error('ERROR GETTING APP ID', error)

      // defaulting to appId in the path
      return path
    }
  }

  const extractQueryParams = (queries = {}) => {
    const queryParams = new URLSearchParams(window.location.search)
    const queryParamsObject = Object.fromEntries(queryParams.entries())
    const paramsPayload = {}

    for (const queryKey in queries) {
      if (Object.prototype.hasOwnProperty.call(queries, queryKey)) {
        const query = queries[queryKey]
        const paramName = query.value
        const datasourceId = query.datasourceId

        if (paramName && queryParamsObject[paramName]) {
          const compositeKey = `${datasourceId}.${queryKey}`
          paramsPayload[compositeKey] = queryParamsObject[paramName]
        }
      }
    }

    return paramsPayload
  }

  const isValidURL = url => {
    try {
      const parsedUrl = new URL(url)

      return !!parsedUrl
    } catch {
      return false
    }
  }

  const handleNativeAppRedirect = (
    bundleId,
    alias,
    componentId,
    paramsPayload
  ) => {
    return new Promise(resolve => {
      const uri = `${bundleId}://${alias}?screen=${componentId}&params=${encodeURIComponent(
        JSON.stringify(paramsPayload)
      )}`

      // Track if the user left the page (indicating successful app launch)
      let userLeft = false

      const handleVisibilityChange = () => {
        if (document.hidden) {
          userLeft = true
          resolve(true) // App likely opened successfully
        }
      }

      const handlePageShow = () => {
        if (!userLeft) {
          resolve(false) // User came back, app didn't open
        }
      }

      // Listen for visibility changes
      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('pageshow', handlePageShow)

      // Attempt to redirect to native app
      window.location.href = uri

      // Cleanup listeners after a reasonable timeout
      setTimeout(() => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('pageshow', handlePageShow)
        if (!userLeft) {
          resolve(false) // Assume app didn't open after timeout
        }
      }, 600)
    })
  }

  const handleFallbackRedirect = (componentId, fallbackURL, queries) => {
    // Validate fallback URL if provided
    if (fallbackURL) {
      if (!isValidURL(fallbackURL)) {
        console.error('Invalid fallback URL:', fallbackURL)

        return
      }
      window.location.replace(fallbackURL)

      return
    }

    try {
      const currentUrl = new URL(window.location.href)
      const params = currentUrl.searchParams

      // Extract query parameters
      const paramsPayload = extractQueryParams(queries)

      if (Object.keys(paramsPayload).length > 0) {
        params.set('params', JSON.stringify(paramsPayload))
      }

      // Update target param and redirect if needed
      if (params.get('target') !== componentId) {
        params.set('target', componentId)
        const fullUrl = `${currentUrl.origin}${
          currentUrl.pathname
        }?${params.toString()}`
        console.log('Redirecting to:', fullUrl)
        window.location.replace(fullUrl)
      }
    } catch (error) {
      console.error('Error handling fallback redirect:', error)
    }
  }

  const handleRedirect = async (alias, app) => {
    if (!alias || !app) {
      console.warn('Missing alias or app data for redirect')

      return
    }

    setRedirecting(true)

    try {
      const userAgent = window.navigator.userAgent
      const agent = getAgent(userAgent)

      const bundleIdMap = {
        android: app?.androidSettings?.bundleId,
        ios: app?.iosSettings?.bundleId,
      }

      const bundleId = bundleIdMap[agent]
      const deepLinkConfig = app.deepLinking?.aliases?.[alias]

      if (!deepLinkConfig) {
        console.warn(`No deep link configuration found for alias "${alias}"`)
        setRedirecting(false)

        return
      }

      const { componentId, query: queries } = deepLinkConfig

      if (!componentId) {
        console.warn(`No componentId found for alias "${alias}"`)
        setRedirecting(false)

        return
      }

      const { androidFallbackURL, iosFallbackURL, fallbackToPWA } =
        app?.deepLinking || {}

      let fallbackURL = null
      if (!fallbackToPWA) {
        fallbackURL =
          agent === 'android'
            ? androidFallbackURL
            : agent === 'ios'
            ? iosFallbackURL
            : null
      }

      const paramsPayload = extractQueryParams(queries)

      // Handle different agents
      if (agent === 'web') {
        console.log('Web agent detected, redirecting to fallback')
        handleFallbackRedirect(componentId, fallbackURL, queries)

        return
      }

      // For mobile agents, try native app first
      if (bundleId && (agent === 'android' || agent === 'ios')) {
        console.log(`Attempting to open native app with bundle ID: ${bundleId}`)

        const nativeAppOpened = await handleNativeAppRedirect(
          bundleId,
          alias,
          componentId,
          paramsPayload
        )

        if (!nativeAppOpened) {
          console.log('Native app failed to open, falling back')
          handleFallbackRedirect(componentId, fallbackURL, queries)
        }
      } else {
        // No bundle ID available, go straight to fallback
        console.log('No bundle ID available, using fallback')
        handleFallbackRedirect(componentId, fallbackURL, queries)
      }
    } catch (error) {
      console.error('Error in handleRedirect:', error)
      setRedirecting(false)
    } finally {
      // Reset redirecting state after a delay to prevent UI flicker
      setTimeout(() => setRedirecting(false), 1000)
    }
  }

  const requestApp = async () => {
    try {
      const appId = await getAppId()

      const app = await fetchApp(appId)
      setApp(app)
      document.title = app.name
      if (route && path) {
        handleRedirect(route, app)
      }

      if (path && app) {
        handleRedirect(path, app)
      }
    } catch (error) {
      console.error('ERROR GETTING APP ID', error)
      setError(true)
    }
  }

  useEffect(() => {
    requestApp()
  }, [])

  if (error) {
    return <NotFound />
  }

  if (!app || redirecting) {
    return <Loading expanded />
  }

  const appId = app.id

  const { attributionComponent } = getAdaloAttributionSettings(
    app,
    windowWidth
  )

  let size = null
  const previewType = getPreviewType(app)

  if (previewType === 'web') {
    size = size || WEB
  }
  const useNewRN = shouldUseNewReactNative(app)
  if (size === WEB) {
    const offsetTop = attributionComponent === 'footer' ? 0 : 74

    return (
      <div>
        <div className="web-renderer-wrapper">
          <Renderer
            app={app}
            offsetTop={offsetTop}
            previewType={previewType}
            useNewRN={useNewRN}
          />
        </div>
        {attributionComponent === 'badge' && <AdaloBadge />}
      </div>
    )
  }

  if (windowWidth < MIN_SCREEN_WIDTH_SAFE_FOR_FRAME) {
    return (
      <>
        <Renderer
          analytics
          app={app}
          hideAppFooter={attributionComponent !== 'footer'}
          size={DEFAULT_SIZE}
          previewType={previewType}
          useNewRN={useNewRN}
        />
      </>
    )
  }

  size = size || DEFAULT_SIZE

  return (
    <React.Fragment>
      <Stripe branding={app.branding} className="top-stripe" />
      <div className="preview">
        <AppDetails app={app} />
        <Frame appId={appId} size={size} />
      </div>
      {attributionComponent === 'badge' && <AdaloBadge />}
    </React.Fragment>
  )
}

export default Preview
